import { AccordionDetails, Button, FormControl, Grid, Paper, Stack, styled, TextField } from '@mui/material';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import { sendRequest } from 'src/hooks/useFetch';
import { setConfirmExitValue } from 'src/store/confirmExitValue';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { setMessage } from 'src/store/Message';
import { ISIVReportConfirmPage } from 'src/types/SIVReport';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import { formatInput } from 'src/util/STR';
import { useFieldTranslation } from '../../fields/useFieldTranslation';
import {
  ISignaturePageMandatory,
  ISIVReportMandatory,
  ISIVReportModifiedKeys,
  ISIVReportModifiedKeysOfKeys,
  MandatoryData
} from './useSivReport';
import { hideLoader, showLoader } from 'src/store/Loader';
import { useLazySetESignQuery } from 'src/services/ESignAPI';
import { useSelectedAppInfo } from 'src/util/SelectedAppInfo';
import ESignConsentReAuth from 'src/components/common/ESignConsentReAuth';
import { useUser } from 'src/components/hooks/useUser';
import { useValidUserForReAuth } from 'src/components/hooks/useValidUserForReAuth';
import { setIsSaveButtonDisable } from 'src/store/sivReport';

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary
}));

export interface ISignaturePage {
  getSivReportData: (key: ISIVReportModifiedKeysOfKeys, add: boolean) => void;
  saveDisable: boolean;
  isUpdate: boolean;
  confirmPageData: ISIVReportConfirmPage;
  updateConfirmPage: (data: ISIVReportConfirmPage) => void;
  setIsChange: (step: ISIVReportModifiedKeys, key: ISIVReportModifiedKeysOfKeys, val: boolean) => void;
  isChange: boolean;
  updateSIVReportMandatoryData: (key: keyof ISIVReportMandatory, value: MandatoryData) => void;
  mandatory: ISignaturePageMandatory;
  sivReportSavedId: string;
  checkedInStartDate: any;
  validateAllAccordionForSign: any;
  isPublished: boolean;
  SignatureBreaking: () => void;
  notifyUser: string;
}

const ConfirmPage: React.FunctionComponent<ISignaturePage> = ({
  saveDisable,
  getSivReportData,
  confirmPageData,
  isUpdate,
  isChange,
  updateConfirmPage,
  setIsChange,
  mandatory,
  updateSIVReportMandatoryData,
  checkedInStartDate,
  validateAllAccordionForSign,
  isPublished,
  SignatureBreaking,
  notifyUser
}) => {
  const [openSignConsent, setOpenSignConsent] = useState(false);
  const [signControlObject, setSignControlObject] = useState({
    is_prepared_sign: false,
    is_reviewed_sign: false,
    is_approved_sign: false
  });
  const { isValidUserForReAuth } = useValidUserForReAuth();
  const { selectedAppSection } = useSelectedAppInfo();
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const user = useUser();
  const { getPreferredDateTimeInLocal } = usePreferedDateFormat();
  const dispatch = useDispatch();
  const dispatchApp = useAppDispatch();
  const { checkPermission } = usePermissionWrapper();
  const isDisable = checkPermission('update_siv_report');
  const selectedRowData: any = useAppSelector(state => state.sivReport.selectedRowData);
  const isNotifyUser = notifyUser == user.user ? true : false;
  const { sivcDataUpdateSuccessMsg, sivcMendatoryInfoErrorMsg, reportConfirm, applicationModuleSection } =
    useFieldTranslation();
  const saveButtonDisable: any = useAppSelector(state => state.sivReport.disableSaveButtonProps);
  const [setESignature] = useLazySetESignQuery();
  const updateConfirmData = async () => {
    const options = {
      site_id: selectedSite,
      study_id: studyId,

      data: {
        updated_by: user.user,
        monitoring_visit_id: selectedRowData?.id,
        siv_report: {
          step_three: { confirm: { siv_report_additional_comments: confirmPageData.siv_report_additional_comments } }
        }
      },

      pathVariables: {
        country: selectedCountry,
        appName: process.env.REACT_APP_NAME
      }
    };
    try {
      if (isChange) {
        if (isUpdate) {
          await sendRequest('update_siv_report', options);
          if (isPublished) {
            SignatureBreaking();
          }
          dispatchApp(setMessage({ msgDetails: sivcDataUpdateSuccessMsg, severity: 'success' }));
          setConfirmExitValue(dispatch, false);
          getSivReportData('confirmPage', false);
          setIsChange('Step3', 'confirmPage', false);
        } else {
          await sendRequest('create_siv_report', options);
          dispatchApp(setMessage({ msgDetails: sivcDataUpdateSuccessMsg, severity: 'success' }));
          setConfirmExitValue(dispatch, false);
          getSivReportData('confirmPage', true);
          setIsChange('Step3', 'confirmPage', false);
        }
      } else {
        dispatchApp(setMessage({ msgDetails: 'No data modified', severity: 'info' }));
      }
      dispatch(setIsSaveButtonDisable(true));
      dispatch(setIsSaveButtonDisable({ ...saveButtonDisable, confirm: true }));
    } catch (e) {
      //dispatchApp(setMessage({ msgDetails: SIVCerrorMsg, severity: 'error' }));
    }
  };

  // useEffect(()=>{
  //   getSivReportData('confirmPage', false);
  // },[])
  const [commentsError, setCommentsError] = useState(false);

  const handleSave = () => {
    if (
      confirmPageData.siv_report_additional_comments === '' ||
      confirmPageData.siv_report_additional_comments === null
    ) {
      dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
      setCommentsError(true);
    } else {
      updateConfirmData();
    }
  };

  const handleChangeComments = (e: any) => {
    const val = e.target.value;
    const formattedVal = typeof val === 'string' ? formatInput(val) : val;
    updateConfirmPage({ ...confirmPageData, [e.target.name]: formattedVal });    
    dispatch(setIsSaveButtonDisable({ ...saveButtonDisable, confirm: false }));
    setConfirmExitValue(dispatch, true);
    if (val) {
      setCommentsError(false);
    } else {
      setCommentsError(true);
    }
  };
  const handleSivReportSign = async (data: any = {}) => {
    if (data.success) {
      if (!isValidUserForReAuth(data.records.user_name)) {
        dispatch(setMessage({ msgDetails: `Please e-sign with the logged in username`, severity: 'error' }));
      } else {
        setOpenSignConsent(false);
        dispatch(showLoader());
        let dataObj = {
          id: confirmPageData?.id,
          username: data.records.user_name,
          password: data.records.password,
          module_name: applicationModuleSection.sivReport,
          name_of_signature: '',
          name_of_date_field: '',
          columns_to_change: {}
        };
        if (signControlObject.is_prepared_sign) {
          dataObj = {
            ...dataObj,
            name_of_signature: 'siv_report_prepared_signature',
            name_of_date_field: 'siv_report_prepared_date',
            columns_to_change: {
              ...dataObj.columns_to_change,
              siv_report_prepared_reason: data.records.sign_reason,
              siv_report_prepared_print_name: user.user
            }
          };
        } else if (signControlObject.is_reviewed_sign) {
          dataObj = {
            ...dataObj,
            name_of_signature: 'siv_report_reviewed_signature',
            name_of_date_field: 'siv_report_reviewed_date',
            columns_to_change: {
              ...dataObj.columns_to_change,
              siv_report_reviewed_reason: data.records.sign_reason,
              siv_report_reviewed_print_name: user.user
            }
          };
        } else if (signControlObject.is_approved_sign) {
          dataObj = {
            ...dataObj,
            name_of_signature: 'siv_report_approved_signature',
            name_of_date_field: 'siv_report_approved_date',
            columns_to_change: {
              ...dataObj.columns_to_change,
              siv_report_approved_reason: data.records.sign_reason,
              siv_report_approved_print_name: user.user
            }
          };
        }
        const signatureResponse: any = await setESignature({
          site_id: selectedSite,
          study_id: studyId,
          country: selectedCountry,
          application: process.env.REACT_APP_NAME,
          application_section: selectedAppSection,
          application_module_section: applicationModuleSection.sivReport,
          data: dataObj
        });
        if (signatureResponse.isSuccess) {
          if (signControlObject.is_prepared_sign) {
            updateSIVReportMandatoryData('confirmPage', {
              ...mandatory,
              siv_report_prepared_signature: false,
              siv_report_prepared_date: false,
              siv_report_prepared_reason: false
            });
          } else if (signControlObject.is_reviewed_sign) {
            updateSIVReportMandatoryData('confirmPage', {
              ...mandatory,
              siv_report_reviewed_signature: false,
              siv_report_reviewed_date: false,
              siv_report_reviewed_reason: false
            });
          } else if (signControlObject.is_approved_sign) {
            updateSIVReportMandatoryData('confirmPage', {
              ...mandatory,
              siv_report_approved_signature: false,
              siv_report_approved_date: false,
              siv_report_approved_reason: false
            });
          }
          dispatch(setMessage({ msgDetails: 'Sign Successful', severity: 'success' }));
          getSivReportData('confirmPage', false);
          setConfirmExitValue(dispatch, false);
        } else if (signatureResponse.isError) {
          dispatch(setMessage({ msgDetails: `Authorization for ${data.records.user_name} failed`, severity: 'error' }));
        }
        setSignControlObject({
          ...signControlObject,
          is_prepared_sign: false,
          is_reviewed_sign: false,
          is_approved_sign: false
        });
        dispatch(hideLoader());
      }
    }
  };
  const checkValidationOnAllAccordion = (forSignName: string) => {
    if (validateAllAccordionForSign()) {
      if (forSignName === 'prepared_by') {
        setSignControlObject({
          ...signControlObject,
          is_prepared_sign: true,
          is_reviewed_sign: false,
          is_approved_sign: false
        });
      } else if (forSignName === 'reviewed_by') {
        setSignControlObject({
          ...signControlObject,
          is_prepared_sign: false,
          is_reviewed_sign: true,
          is_approved_sign: false
        });
      } else {
        setSignControlObject({
          ...signControlObject,
          is_prepared_sign: false,
          is_reviewed_sign: false,
          is_approved_sign: true
        });
      }
      setOpenSignConsent(true);
    } else {
      dispatch(setMessage({ msgDetails: 'Missing mandatory information', severity: 'error' }));
    }
  };

  return (
    <>
      <AccordionDetails className="sq-checklist-tab">
        <Grid container className="checklist-radio-actions">
          <Grid md={12}>
            <Grid container>
              <Grid md={2.5} sx={{ pt: 1 }}>
                {reportConfirm.label1}: <span style={{ color: 'red' }}>* </span>
              </Grid>
              <Grid md={9.5} sx={{ mt: 1 }}>
                {confirmPageData.siv_report_prepared_signature ? (
                  <div>
                    <span className="signedInfo">
                      Signed by <strong>{confirmPageData?.siv_report_prepared_signature}</strong> on{' '}
                      <span color="text.secondary">
                        {getPreferredDateTimeInLocal(confirmPageData?.siv_report_prepared_date)}
                      </span>{' '}
                      <br />
                      <span className="signReasonrow">
                        <span color="text.secondary">Reason:</span> &ldquo;{confirmPageData?.siv_report_prepared_reason}
                        &rdquo;
                      </span>
                    </span>
                  </div>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => checkValidationOnAllAccordion('prepared_by')}
                    disabled={
                      !isDisable ||
                      !checkPermission('siv_report_preparedby') ||
                      !checkPermission('view_siv_report') ||
                      !checkedInStartDate
                    }
                    sx={{ mt: -0.5 }}
                  >
                    Sign
                  </Button>
                )}
              </Grid>
            </Grid>
            <Grid container>
              <Grid md={2.5}>
                {reportConfirm.label2}: <span style={{ color: 'red' }}>* </span>
              </Grid>
              <Grid md={9.5}>
                {confirmPageData.siv_report_reviewed_signature ? (
                  <div>
                    <span className="signedInfo">
                      Signed by <strong>{confirmPageData?.siv_report_reviewed_signature}</strong> on{' '}
                      <span color="text.secondary">
                        {getPreferredDateTimeInLocal(confirmPageData?.siv_report_reviewed_date)}
                      </span>{' '}
                      <br />
                      <span className="signReasonrow">
                        <span color="text.secondary">Reason:</span> &ldquo;{confirmPageData?.siv_report_reviewed_reason}
                        &rdquo;
                      </span>
                    </span>
                  </div>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => checkValidationOnAllAccordion('reviewed_by')}
                    disabled={
                      !isNotifyUser ||
                      !isDisable ||
                      !checkPermission('siv_report_reviewedby') ||
                      !checkPermission('view_siv_report') ||
                      !checkedInStartDate ||
                      !confirmPageData.siv_report_prepared_signature
                    }
                    sx={{ mt: -0.5 }}
                  >
                    Sign
                  </Button>
                )}
              </Grid>
            </Grid>
            <Grid container>
              <Grid md={2.5}>
                {reportConfirm.label3}: <span style={{ color: 'red' }}>* </span>
              </Grid>
              <Grid md={9.5}>
                {confirmPageData.siv_report_approved_signature ? (
                  <div>
                    <span className="signedInfo">
                      Signed by <strong>{confirmPageData?.siv_report_approved_signature}</strong> on{' '}
                      <span color="text.secondary">
                        {getPreferredDateTimeInLocal(confirmPageData?.siv_report_approved_date)}
                      </span>{' '}
                      <br />
                      <span className="signReasonrow">
                        <span color="text.secondary">Reason:</span> &ldquo;{confirmPageData?.siv_report_approved_reason}
                        &rdquo;
                      </span>
                    </span>
                  </div>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => checkValidationOnAllAccordion('final_approval_by')}
                    disabled={
                      !isNotifyUser ||
                      !isDisable ||
                      !checkPermission('siv_report_approvedby') ||
                      !checkPermission('view_siv_report') ||
                      !checkedInStartDate ||
                      !confirmPageData?.siv_report_reviewed_signature
                    }
                    sx={{ mt: -0.5 }}
                  >
                    Sign
                  </Button>
                )}
              </Grid>
            </Grid>
            <Grid container>
              <Grid md={12} sx={{ mt: 0 }}>
                <FormControl>
                  <Item>
                    <TextField
                      id="standard-basic"
                      label={`${reportConfirm.label4}:`}
                      variant="standard"
                      name="siv_report_additional_comments"
                      value={confirmPageData.siv_report_additional_comments || ''}
                      inputProps={{ maxLength: 400 }}
                      onChange={handleChangeComments}
                      error={commentsError}
                      required
                      helperText={commentsError ? 'Required' : null}
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                    />
                  </Item>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
      <Grid container className="fixed-buttons-block">
        <Grid md={12} display="flex" justifyContent="flex-end">
          <Stack spacing={2} direction="row">
            {isDisable ? (
              <Button variant="text" onClick={handleSave} disabled={saveButtonDisable?.confirm || !checkedInStartDate}>
                SAVE
              </Button>
            ) : (
              ''
            )}
          </Stack>
        </Grid>
      </Grid>
      {openSignConsent && (
        <ESignConsentReAuth
          openSignConsent={openSignConsent}
          setOpenSignConsent={setOpenSignConsent}
          handleConfirmSign={handleSivReportSign}
        />
      )}
    </>
  );
};

export default ConfirmPage;
